import { useRef, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Upload, FileSpreadsheet, Loader2, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

// Canonical editable numeric fields on pricing_settings
const PRICING_FIELDS: { key: string; label: string }[] = [
  { key: "roof_price_per_sq_asphalt", label: "Asphalt Shingles ($/sq)" },
  { key: "roof_price_per_sq_metal", label: "Metal Roof ($/sq)" },
  { key: "roof_price_per_sq_flat", label: "Flat Roof ($/sq)" },
  { key: "roof_tearoff_per_sq", label: "Tear-off ($/sq)" },
  { key: "roof_dump_fee_per_sq", label: "Dump Fee ($/sq)" },
  { key: "roof_base_waste_percent", label: "Base Waste (%)" },
  { key: "roof_steep_pitch_extra_percent", label: "Steep Pitch Extra (%)" },
  { key: "siding_price_per_sqft_vinyl", label: "Vinyl Siding ($/sqft)" },
  { key: "siding_price_per_sqft_fiber", label: "Fiber/Hardie Siding ($/sqft)" },
  { key: "soffit_price_per_sqft", label: "Soffit ($/sqft)" },
  { key: "fascia_price_per_ft", label: "Fascia ($/ft)" },
  { key: "gutter_5in_price_per_ft", label: '5" Gutter ($/ft)' },
  { key: "gutter_6in_price_per_ft", label: '6" Gutter ($/ft)' },
  { key: "downspout_price_each", label: "Downspout ($ each)" },
  { key: "gutter_guard_price_per_ft", label: "Gutter Guard ($/ft)" },
  { key: "deck_composite_per_sqft", label: "Deck Composite ($/sqft)" },
  { key: "deck_wood_per_sqft", label: "Deck Wood ($/sqft)" },
  { key: "deck_labor_per_sqft", label: "Deck Labor ($/sqft)" },
  { key: "deck_railing_per_ft", label: "Deck Railing ($/ft)" },
  { key: "deck_stairs_each", label: "Deck Stairs ($ each)" },
  { key: "window_standard_each", label: "Window ($ each)" },
  { key: "window_labor_each", label: "Window Labor ($ each)" },
  { key: "window_trim_per_ft", label: "Window Trim ($/ft)" },
  { key: "paint_interior_per_sqft", label: "Paint Interior ($/sqft)" },
  { key: "paint_exterior_per_sqft", label: "Paint Exterior ($/sqft)" },
  { key: "paint_prep_per_sqft", label: "Paint Prep ($/sqft)" },
  { key: "general_material_markup_percent", label: "General Material Markup (%)" },
  { key: "general_labor_rate_per_hour", label: "General Labor Rate ($/hr)" },
  { key: "overhead_percent", label: "Overhead (%)" },
  { key: "profit_margin_percent", label: "Profit Margin (%)" },
];

function parseCSV(text: string): Record<string, number> {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  const out: Record<string, number> = {};
  // skip header if first cell is "field" or "key"
  const start = /^(field|key|name)\s*,/i.test(lines[0] || "") ? 1 : 0;
  for (let i = start; i < lines.length; i++) {
    const cells = lines[i].split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
    if (cells.length < 2) continue;
    const key = cells[0];
    const val = parseFloat(cells[1].replace(/[$,%\s]/g, ""));
    if (PRICING_FIELDS.some((f) => f.key === key) && !isNaN(val)) {
      out[key] = val;
    }
  }
  return out;
}

export function PricingBulkImport({ onImported }: { onImported?: () => void }) {
  const { user } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [lastImport, setLastImport] = useState<number | null>(null);

  const downloadTemplate = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("pricing_settings")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();

    const rows = ["field,value,description"];
    for (const f of PRICING_FIELDS) {
      const current = (data as any)?.[f.key] ?? "";
      rows.push(`${f.key},${current},"${f.label}"`);
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "pricing-template.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Template downloaded");
  };

  const handleFile = async (file: File) => {
    if (!user) return;
    setBusy(true);
    try {
      const text = await file.text();
      const updates = parseCSV(text);
      const count = Object.keys(updates).length;
      if (count === 0) {
        toast.error("No valid pricing rows found in CSV");
        return;
      }
      const { error } = await supabase
        .from("pricing_settings")
        .upsert(
          { user_id: user.id, ...updates, updated_at: new Date().toISOString() },
          { onConflict: "user_id" }
        );
      if (error) throw error;
      setLastImport(count);
      toast.success(`Imported ${count} pricing rates`);
      onImported?.();
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || "Import failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-primary" />
          Bulk Import Pricing
        </CardTitle>
        <CardDescription>
          Download the template, fill in your rates in Excel/Sheets, save as CSV, and upload it
          back. All fields are optional — only fields you fill in will be updated.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={downloadTemplate} disabled={busy}>
          <Download className="w-4 h-4 mr-2" />
          Download CSV template
        </Button>
        <Button onClick={() => fileRef.current?.click()} disabled={busy}>
          {busy ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Upload className="w-4 h-4 mr-2" />
          )}
          Upload filled CSV
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
        />
        {lastImport !== null && (
          <span className="flex items-center gap-1 text-sm text-muted-foreground">
            <CheckCircle2 className="w-4 h-4 text-primary" />
            Imported {lastImport} fields
          </span>
        )}
      </CardContent>
    </Card>
  );
}
